import ENDPOINTS from "../../constants/apiEndPoints";
import { ApiResponse, axiosInstance } from "../axiosInstance";

export type OrganizationState = "draft" | "active" | "inactive";
export type AdminLocationType = "client" | "branch" | "subBranch";

export type OrganizationsScreenTypes =
  | "organizationsList"
  | "organizationProfile"
  | "activityLog"
  | "messagesLog";

export type OrganizationMedia = {
  url?: string;
  type?: string;
};

export type OrganizationOperatingHours = {
  startTime?: string;
  endTime?: string;
};

export type OrganizationChecklistNotesReset = {
  type?: string;
  time?: string;
};

export type OrganizationApiContact = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
};

export type OrganizationContact = OrganizationApiContact & {
  systemRole?: string | null;
};

export type OrganizationApiLocationNode = {
  _id: string;
  id: string;
  name: string;
  status?: OrganizationState;
  isArchived?: boolean;
  type?: string | null;
  logo?: OrganizationMedia | null;
  mobileLogo?: OrganizationMedia | null;
  address?: string;
  email?: string;
  city?: string;
  phone?: string;
  state?: string;
  country?: string;
  contacts?: OrganizationApiContact[];
  operatingHours?: OrganizationOperatingHours;
  website?: string;
  hpNumber?: string;
  checklistNotesReset?: OrganizationChecklistNotesReset;
  draftId?: string;
  branches?: OrganizationApiLocationNode[];
  subBranches?: OrganizationApiLocationNode[];
};

export type AdminOnBoardingDraft = {
  draftId: string;
  hasOwner?: boolean;
  completedOnboarding?: boolean;
  shouldResyncOnboarding?: boolean;
  lastActivatedAt?: string;
  clients: OrganizationApiLocationNode[];
};

export type OrganizationLocationBase = {
  id: string;
  _id?: string;
  draftId: string;
  name: string;
  icon: string;
  apiIcon?: string;
  type: string | null;
  status: OrganizationState;
  isArchived?: boolean;
  address: string;
  email?: string | null;
  phone?: string | null;
  logo: OrganizationMedia | null;
  mobileLogo: OrganizationMedia | null;
  contacts: OrganizationContact[];
  city?: string;
  state?: string;
  country?: string;
  website?: string;
  hpNumber?: string;
  checklistNotesReset?: OrganizationChecklistNotesReset;
  operatingHours?: OrganizationOperatingHours;
  clientId?: string;
  branchId?: string;
};

export type OrganizationLocation = OrganizationLocationBase & {
  isClient: boolean;
  isBranch: boolean;
  isSubBranch: boolean;
};

export type OrganizationSubBranch = OrganizationLocationBase & {
  isClient: false;
  isBranch: false;
  isSubBranch: true;
  clientId: string;
  branchId: string;
};

export type OrganizationBranch = OrganizationLocationBase & {
  isClient: false;
  isBranch: true;
  isSubBranch: false;
  clientId: string;
  subBranches: OrganizationSubBranch[];
};

export type OrganizationRoot = OrganizationLocationBase & {
  isClient: true;
  isBranch: false;
  isSubBranch: false;
  branches: OrganizationBranch[];
};

export type OrganizationNode =
  | OrganizationRoot
  | OrganizationBranch
  | OrganizationSubBranch;

export type OrganizationTableRow =
  | (OrganizationRoot & { organizationLevel: "network" })
  | (OrganizationBranch & { organizationLevel: "branch" })
  | (OrganizationSubBranch & { organizationLevel: "subBranch" });

export const handleGetOrganizationDraftsService = async () => {
  const response = await axiosInstance.get<ApiResponse<AdminOnBoardingDraft[]>>(
    ENDPOINTS.GET_ADMIN_ON_BOARDING_DRAFTS,
  );

  return response?.data?.data;
};

export const handleDeleteOrganizationDraftService = async ({
  draftId,
}: {
  draftId: string;
}) => {
  const response = await axiosInstance.delete<ApiResponse<unknown>>(
    ENDPOINTS.DELETE_ADMIN_ON_BOARDING_DRAFT(draftId),
  );

  return response?.data;
};

export const handleUpdateAdminLocationStatusService = async ({
  locationType,
  locationId,
  status,
}: {
  locationType: AdminLocationType;
  locationId: string;
  status: Exclude<OrganizationState, "draft">;
}) => {
  const response = await axiosInstance.patch<ApiResponse<unknown>>(
    ENDPOINTS.UPDATE_ADMIN_LOCATION_STATUS(locationType, locationId),
    { status },
  );

  return response?.data;
};

export const handleArchiveAdminLocationService = async ({
  locationType,
  locationId,
  archive,
}: {
  locationType: AdminLocationType;
  locationId: string;
  archive: boolean;
}) => {
  const response = await axiosInstance.patch<ApiResponse<unknown>>(
    ENDPOINTS.ARCHIVE_ADMIN_LOCATION(locationType, locationId),
    { archive },
  );

  return response?.data;
};
